import { createContext, type HTMLProps, type MutableRefObject, type RefCallback, use } from 'react'

type DropdownMenuItemHtmlProps = Omit<HTMLProps<HTMLElement>, 'ref'> & {
    ref?: RefCallback<HTMLElement>
}

type DropdownMenuContextValue = {
    getItemProps: (userProps: DropdownMenuItemHtmlProps) => DropdownMenuItemHtmlProps
    closeMenu: () => void
    /**
     * Whether this menu is skipping its animation, so a submenu opened from it makes the same choice.
     * A menu told to hold still that spawns an animating panel is worse than either alone.
     */
    skipMotion: boolean
    /**
     * The row a searching panel's Enter would choose. It is null in a menu without a search field, where
     * the row in play is the focused one and the browser marks it for us.
     */
    activeIndex: number | null
    /**
     * Moves that mark, for the pointer: the row under it and the row Enter would choose are one and the
     * same, or a hovered row and a marked one are both lit and the mark wins. Absent where nothing searches.
     */
    setActiveIndex?: (index: number) => void
    /** Prefix for item ids, so a search field can point `aria-activedescendant` at the active row. */
    itemIdPrefix: string
    /**
     * The submenu of this menu that currently holds the panel slot, if any. A submenu opening while the
     * slot is taken is the pointer walking from one trigger to the next, and appears without animating.
     */
    openSubmenuRef: MutableRefObject<string | null>
}

export const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(null)

export function useDropdownMenuContext() {
    return use(DropdownMenuContext)
}
